import React, { useEffect, useRef, useState } from 'react';
import cambioEstadoService from '../../services/cambioEstadoService';
import { obtenerSiguienteEstadoART } from '../../services/art/artEstadoService';
import { actualizarComentariosTramite } from '../../services/tramiteService';
import { toast } from 'react-toastify';
import { obtenerArchivosPorTramite, descargarArchivo, eliminarArchivo, subirArchivo } from '../../services/archivoService';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import SaveIcon from '@mui/icons-material/Save';
import DescriptionIcon from '@mui/icons-material/Description';

// ── Persistencia ─────────────────────────────────────────────────────────────
const PREFIJO = '[ESPERA-ANEXO]';

const parsearNotaGuardada = (comentarios = '') => {
    const texto = String(comentarios || '').trim();
    if (!texto.startsWith(PREFIJO)) return null;
    const obsMatch = texto.match(/Observaciones: ([\s\S]*?)$/);
    return { observaciones: obsMatch ? obsMatch[1].trim() : '' };
};

const formatearNota = (observaciones) => {
    let nota = PREFIJO;
    if (observaciones.trim()) nota += `\nObservaciones: ${observaciones.trim()}`;
    return nota;
};

// ── Componente ────────────────────────────────────────────────────────────────
function GestorEsperaAnexo({ tramite, onAvanzar }) {
    const [observaciones, setObservaciones] = useState('');

    const [archivos, setArchivos] = useState([]);
    const [cargandoArchivos, setCargandoArchivos] = useState(false);
    const [subiendoArchivos, setSubiendoArchivos] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef(null);

    const [guardandoNota, setGuardandoNota] = useState(false);
    const [avanzando, setAvanzando] = useState(false);

    useEffect(() => {
        const notaGuardada = parsearNotaGuardada(tramite.comentarios);
        if (notaGuardada) setObservaciones(notaGuardada.observaciones);
        cargarArchivos();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const cargarArchivos = async () => {
        setCargandoArchivos(true);
        try {
            const archivosBackend = await obtenerArchivosPorTramite(tramite.idTramite);
            setArchivos(archivosBackend.map(a => ({
                id: `backend_${a.idArchivo}`,
                idArchivo: a.idArchivo,
                nombre: a.nombre,
                tamaño: (a.tamaño / (1024 * 1024)).toFixed(2) + ' MB',
                estado: 'subido',
                esDelBackend: true,
            })));
        } catch {
            toast.error('No se pudieron cargar los archivos adjuntos.');
        } finally {
            setCargandoArchivos(false);
        }
    };

    // ── Archivos ──────────────────────────────────────────────────────────────

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(e.type === 'dragenter' || e.type === 'dragover');
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files?.[0]) handleSeleccionarArchivos({ target: { files: e.dataTransfer.files } });
    };

    const handleSeleccionarArchivos = (e) => {
        const nuevos = Array.from(e.target.files).map((file, idx) => ({
            id: `local_${Date.now()}_${idx}`,
            nombre: file.name,
            tamaño: (file.size / (1024 * 1024)).toFixed(2) + ' MB',
            archivo: file,
            estado: 'pendiente',
            esDelBackend: false,
        }));
        setArchivos(prev => [...prev, ...nuevos]);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleSubirArchivos = async () => {
        const pendientes = archivos.filter(a => a.estado === 'pendiente');
        if (!pendientes.length) { toast.warn('No hay archivos pendientes'); return; }
        setSubiendoArchivos(true);
        for (const a of pendientes) {
            try {
                await subirArchivo(tramite.idTramite, a.archivo);
                setArchivos(prev => prev.map(x => x.id === a.id ? { ...x, estado: 'subido' } : x));
            } catch {
                setArchivos(prev => prev.map(x => x.id === a.id ? { ...x, estado: 'error' } : x));
            }
        }
        toast.success('Archivos procesados');
        setSubiendoArchivos(false);
    };

    const handleDescargarArchivo = async (a) => {
        try { await descargarArchivo(a.idArchivo, a.nombre); }
        catch { toast.error('Error al descargar'); }
    };

    const handleEliminarArchivo = async (id) => {
        const a = archivos.find(x => x.id === id);
        if (!a) return;
        if (a.esDelBackend) {
            try {
                await eliminarArchivo(a.idArchivo);
                setArchivos(prev => prev.filter(x => x.id !== id));
                toast.success('Archivo eliminado');
            } catch { toast.error('Error al eliminar'); }
        } else {
            setArchivos(prev => prev.filter(x => x.id !== id));
        }
    };

    // ── Guardar nota sin avanzar ──────────────────────────────────────────────

    const handleGuardarNota = async () => {
        setGuardandoNota(true);
        try {
            await actualizarComentariosTramite(tramite.idTramite, formatearNota(observaciones));
            toast.success('Progreso guardado correctamente.');
        } catch {
            toast.error('Error al guardar el progreso.');
        } finally {
            setGuardandoNota(false);
        }
    };

    // ── Validar y avanzar ─────────────────────────────────────────────────────

    const handleValidar = async () => {
        if (archivos.some(a => a.estado === 'pendiente')) {
            toast.error('Subí todos los archivos pendientes antes de avanzar.');
            return;
        }
        setAvanzando(true);
        try {
            const siguienteEstado = await obtenerSiguienteEstadoART(tramite.nombreEstadoActual);
            const comentarioFinal = observaciones.trim()
                ? `Anexo validado. ${observaciones.trim()}`
                : 'Anexo validado. Avanzando a Comisión Médica.';
            await cambioEstadoService.cambiarEstado(tramite.idTramite, {
                idEstado: siguienteEstado.id || siguienteEstado.idEstado,
                comentario: comentarioFinal,
            });
            toast.success('Anexo validado. Avanzando al siguiente estado.');
            onAvanzar();
        } catch (error) {
            toast.error('Error al avanzar: ' + (error.response?.data?.message || error.message));
        } finally {
            setAvanzando(false);
        }
    };

    const hayArchivosPendientes = archivos.some(a => a.estado === 'pendiente');

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <div className="card shadow-lg border-0 rounded-4 p-4 mb-4">

            {/* Encabezado */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="fw-bold text-warning mb-0">Pendiente de Anexo</h4>
                <span className="badge bg-warning text-dark rounded-pill px-3 py-2">
                    <AssignmentTurnedInIcon fontSize="small" className="me-1" />
                    En espera
                </span>
            </div>

            {/* Info */}
            <div className="alert alert-warning border-0 rounded-3 mb-4">
                <strong>Información:</strong>
                <p className="mb-0 mt-1">
                    Se aguarda la recepción del anexo antes de iniciar la Comisión Médica.
                    Adjuntá el documento cuando esté disponible y agregá observaciones si es necesario.
                </p>
            </div>

            {/* Observaciones */}
            <div className="mb-4">
                <h6 className="fw-semibold text-primary mb-2">
                    <DescriptionIcon fontSize="small" className="me-1 mb-1" />
                    Observaciones
                </h6>
                <textarea
                    className="form-control"
                    rows="4"
                    placeholder="Ej: Fecha estimada de recepción del anexo, aclaraciones sobre el documento pendiente..."
                    value={observaciones}
                    onChange={e => setObservaciones(e.target.value)}
                    style={{ borderRadius: '0.5rem',
                        border: '1px solid #e0e0e0', 
                        lineHeight: '1.5' }}
                />
            </div>

            {/* Guardar sin avanzar */}
            <div className="d-flex justify-content-end mb-4">
                <button
                    className="btn btn-outline-primary d-flex align-items-center gap-2 rounded-pill px-4"
                    onClick={handleGuardarNota}
                    disabled={guardandoNota}
                >
                    <SaveIcon fontSize="small" />
                    {guardandoNota ? 'Guardando...' : 'Guardar progreso (sin avanzar)'}
                </button>
            </div>

            <hr className="my-1" />

            {/* Archivos adjuntos */}
            <div className="mt-3">
                <h6 className="fw-semibold text-primary mb-1">
                    <AttachFileIcon fontSize="small" className="me-1" />
                    Archivos adjuntos
                </h6>
                <p className="text-muted mb-3" style={{ fontSize: '0.83rem' }}>
                    Adjuntá el anexo u otros documentos relevantes.
                </p>

                {cargandoArchivos ? (
                    <p className="text-muted">Cargando archivos...</p>
                ) : (
                    <>
                        {archivos.length > 0 && (
                            <div className="list-group mb-3">
                                {archivos.map(a => (
                                    <div key={a.id} className="list-group-item d-flex justify-content-between align-items-center py-2">
                                        <div>
                                            <AttachFileIcon fontSize="small" className="me-2 text-muted" />
                                            <span className="fw-semibold">{a.nombre}</span>
                                            <small className="text-muted ms-2">({a.tamaño})</small>
                                            <span className={`badge ms-2 ${a.estado === 'subido' ? 'bg-success' : a.estado === 'error' ? 'bg-danger' : 'bg-warning text-dark'}`}>
                                                {a.estado === 'subido' ? 'subido ✓' : a.estado}
                                            </span>
                                        </div>
                                        <div className="d-flex gap-1">
                                            {a.esDelBackend && (
                                                <button className="btn btn-sm btn-outline-primary" onClick={() => handleDescargarArchivo(a)} title="Descargar">
                                                    <DownloadIcon fontSize="small" />
                                                </button>
                                            )}
                                            <button className="btn btn-sm btn-outline-danger" onClick={() => handleEliminarArchivo(a.id)} title="Eliminar">
                                                <DeleteIcon fontSize="small" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div
                            className={`border rounded-3 p-4 text-center ${dragActive ? 'border-warning bg-warning bg-opacity-10' : 'border-dashed'}`}
                            onDragEnter={handleDrag}
                            onDragLeave={handleDrag}
                            onDragOver={handleDrag}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                            style={{ cursor: 'pointer', borderStyle: 'dashed', borderWidth: '2px', borderColor: '#ffc107' }}
                        >
                            <input type="file" multiple ref={fileInputRef} onChange={handleSeleccionarArchivos} className="d-none" />
                            <CloudUploadIcon className="text-muted mb-2" style={{ fontSize: '2.5rem', display: 'block', margin: '0 auto' }} />
                            <p className="mb-0 text-muted">Arrastrá archivos aquí o hacé clic para seleccionar</p>
                        </div>

                        {hayArchivosPendientes && (
                            <div className="d-flex justify-content-center mt-3">
                                <button
                                    className="btn btn-primary rounded-pill px-4"
                                    onClick={handleSubirArchivos}
                                    disabled={subiendoArchivos}
                                >
                                    {subiendoArchivos
                                        ? 'Subiendo...'
                                        : `Subir ${archivos.filter(a => a.estado === 'pendiente').length} archivo(s)`}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Validar y avanzar */}
            <div className="d-flex flex-column align-items-center mt-4 pt-3 border-top gap-2">
                <button
                    className="btn btn-success d-flex align-items-center gap-2 rounded-pill px-5 py-2 fw-semibold"
                    onClick={handleValidar}
                    disabled={avanzando || subiendoArchivos || hayArchivosPendientes}
                    title={hayArchivosPendientes ? 'Subí todos los archivos pendientes primero' : 'Validar anexo y avanzar a Comisión Médica'}
                >
                    <AssignmentTurnedInIcon fontSize="small" />
                    {avanzando ? 'Avanzando...' : 'Validar Anexo y Avanzar'}
                </button>
                {hayArchivosPendientes && (
                    <small className="text-muted">Subí todos los archivos pendientes antes de avanzar.</small>
                )}
            </div>
        </div>
    );
}

export default GestorEsperaAnexo;
